import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { AbstractControl, AsyncValidator, NG_ASYNC_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from "@angular/forms";
import { ControlBase } from "@mipro/custom-controls";
import { SquadType } from "@project/models/squad";
import { SelectItem, SelectItemGroup } from "primeng/api";
import { map, Observable, of, Subject } from "rxjs";
import { PlayerService } from "../../project/player/player.service";
import { OrganizationGroup } from "./organization-group";
import { UserHelperService } from "@main/users/user-helper.service";
import { LinkUserService } from "@main/users";
import { EntityId } from "@bbwt/interfaces";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
    selector: "organization-groups",
    template: `<p-multiSelect
        [styleClass]="isRequired ? 'required-multiselect' : ''"
        [group]="true"
        [(ngModel)]="value"
        [options]="options"
        [showToggleAll]="false"
        (onChange)="itemToggled($event.itemValue.value)"
        (onBlur)="onTouched()"
        [disabled]="loadingGroups"
        appendTo="body"
        display="chip">
        <ng-template pTemplate="selectedItems">
            <div class="p-multiselect-token" *ngFor="let chip of chipValues">
                <span class="p-multiselect-token-label">{{ chip.label }}</span>
                <span class="p-multiselect-token-icon pi pi-times-circle" (click)="unselectSquad(chip.squadId)"></span>
            </div>
            <div *ngIf="chipValues?.length === 0">
                <span>Select</span>
            </div>
        </ng-template>
    </p-multiSelect> `,
    styleUrls: ["./organization-group-control.scss"],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: OrganizationGroupControlComponent,
            multi: true
        },
        {
            provide: NG_ASYNC_VALIDATORS,
            useExisting: OrganizationGroupControlComponent,
            multi: true
        }
    ]
})
export class OrganizationGroupControlComponent extends ControlBase<string[]> implements OnInit, AsyncValidator {
    private _value: string[];
    private _options: SelectItemGroup[] = [];
    private _squadToGroup: { [key: string]: { group: OrganizationGroup; options: SelectItem[] } } = {};
    private _organizationId: string;
    private _loadingGroupSubject: Subject<void> = new Subject<void>();

    chipValues: { squadId: string; label: string }[] = [];
    loadingGroups = true;
    private originalOptions: SelectItemGroup[];

    @Input()
    set organizationId(orgId: string) {
        if (orgId != this._organizationId) {
            this.updateOrganization(orgId);
        }
    }

    @Input()
    defaultSquadType: SquadType;
    @Input()
    singleOrganization = false;

    @Input() editUser = false;
    @Input() editUserGroups = [];

    @Input() maxSelect: number;

    @Input() isRequired: boolean = false;

    @Output() readonly valueChanged = new EventEmitter();
    @Output() readonly organizationsChanged = new EventEmitter();
    @Output() readonly organizationsIds = new EventEmitter();

    @Output() readonly deleteGroupRestriction = new EventEmitter<void>();

    private _orgGroups: OrganizationGroup[] = [];

    get value() {
        return this._value;
    }
    set value(val: string[]) {
        this._value = val;
        this.onChange(val);
    }

    constructor(
        private playerService: PlayerService,
        private linkUserService: LinkUserService,
        private userHelperService: UserHelperService,
        private broadcastService: BroadcastService
    ) {
        super();

        this.broadcastService
            .on(LinkUserService.UserSelectedOrganizationChangedEventName)
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                if (!this.singleOrganization && !this._organizationId) {
                    this.resetSelection();
                    this.updateOrgGroupsList();
                }
            });
    }

    get options() {
        return this._options;
    }

    writeValue(obj: string[]): void {
        this._value = obj;
    }

    public resetSelection() {
        this.chipValues = [];
        this.value = [];
    }

    validate(control: AbstractControl): Observable<ValidationErrors> {
        if (this.loadingGroups) {
            return this._loadingGroupSubject.asObservable().pipe(map(() => this.validateSync(control)));
        }

        return of(this.validateSync(control));
    }

    private validateSync(control: AbstractControl): ValidationErrors {
        if (this.singleOrganization) {
            return this.value?.some(squadId => this._squadToGroup[squadId] && this._squadToGroup[squadId].group.id === this._organizationId)
                ? null
                : { required: true };
        }

        if (this.maxSelect === 1) {
            return this.value.length === 1 ? null : { required: true };
        }

        return this.value?.length > 0 ? null : { required: true };
    }

    enableAll() {
        this._options.forEach(g => g.items.forEach(item => (item.disabled = false)));
    }

    itemToggled(squadId: string) {
        if (!this._squadToGroup[squadId]) {
            return;
        }
        const itemEnabled = this._value.findIndex(x => x === squadId) >= 0;

        const chip = this.createChip(squadId);
        if (itemEnabled) {
            this.chipValues.push(chip);
        } else {
            this.chipValues = this.chipValues.filter(c => c.squadId !== chip.squadId);
        }

        this.valueChanged.emit(this.value);

        const selectedOrgsIds = this.chipValues.map(c => this._squadToGroup[c.squadId].group.id);        
        this.organizationsChanged.emit(selectedOrgsIds);

        const selectedOrgsMap: { [key: string]: string[] } = {};

        this.chipValues.forEach(c => {
            const orgId = this._squadToGroup[c.squadId].group.id;
            if (!selectedOrgsMap[orgId]) {
                selectedOrgsMap[orgId] = [];
            }
            selectedOrgsMap[orgId].push(c.squadId);
        });

        const selectedOrganizationIds = [
            ...new Set(this.chipValues.map(c => this._squadToGroup[c.squadId].group.id))
        ];

        this.organizationsIds.emit(selectedOrganizationIds);
    }

    async ngOnInit() {
        if (!this.singleOrganization && !this._organizationId) {
            await this.updateOrgGroupsList();
            this.value?.forEach(i => this.itemToggled(i));
        }
    }

    async unselectSquad(squadId: string) {
        this.value = this.value.filter(id => id !== squadId);
        this.itemToggled(squadId);
    }

    private async updateOrgGroupsList() {
        this.loadingGroups = true;
        try {
            this._orgGroups = await this.playerService.getOrganizationGroups(this._organizationId);

            let currentUserLogins = await this.linkUserService.getUserLogins();
            let currentUserOrganizations = [];
            currentUserLogins.forEach(ul => {
                currentUserOrganizations = currentUserOrganizations.concat(ul.organizations);
            });

            this._orgGroups = this._orgGroups.filter(o => currentUserOrganizations.findIndex(uo => uo.id === o.id) >= 0);

            const userCurrentOrgIndex = this._orgGroups.findIndex(x => x.id === this.userHelperService.hashedOrganizationId);
            const userCurrentOrg = this._orgGroups.splice(userCurrentOrgIndex, 1)[0];
            this._orgGroups.unshift(userCurrentOrg);

            this._options = this._orgGroups.map(og => {
                const items: SelectItem[] = [];
                og.groups.forEach(squad => {
                    if (squad.type !== SquadType.UnassignedPatients && !squad.archived) {
                        this._squadToGroup[squad.id] = { group: og, options: items };
                        items.push({ label: squad.name, value: squad.id });
                    }
                });
                return { label: `${og.governingBodyName} - ${og.name}`, value: og.id, items };
            });

            this.originalOptions = [...this._options];
        } finally {
            this.loadingGroups = false;
            this._loadingGroupSubject?.next();
            this._loadingGroupSubject.complete();
        }
    }

    private createChip(squadId: string) {
        const organizationGroup = this._squadToGroup[squadId].group;
        const squad = organizationGroup?.groups.find(s => s.id === squadId);

        return { squadId, label: `${organizationGroup?.name} - ${squad?.name}` };
    }

    async updateOrganization(orgId: string) {
        this._organizationId = orgId;
        await this.updateOrgGroupsList();

        const currentGroup = this._orgGroups.find(og => og.id === orgId);
        let setDefault = this.singleOrganization && this.defaultSquadType != null && this.defaultSquadType !== SquadType.UserDefined;
        this.chipValues = [];

        this.value
            ?.filter(squadId => this._squadToGroup[squadId]?.group.id === orgId)
            .forEach(squadId => {
                this.itemToggled(squadId);
                setDefault = false;
            });

        if (setDefault) {
            const defaultSquad = currentGroup?.groups.find(s => s.type === this.defaultSquadType);

            if (!!defaultSquad) {
                this.value = [...(this.value ?? []), defaultSquad.id];
                this.itemToggled(defaultSquad.id);
            }
        }
    }
}
